import React, { Fragment, useCallback, useMemo, useState, type PropsWithChildren } from 'react';

// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql, useMutation } from 'react-relay';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import RelayDataID from '@atlassian/relay-data-id';
import { useFlagService } from '@atlassian/jira-flags';

import { projectPickerEntrypoint as AtlasProjectEditViewEntrypoint } from '@atlassian/project-picker/projectPickerEntrypoint';
import type { ProjectPickerWithEntrypointProps } from '@atlassian/project-picker/ProjectPicker';
import { AtlasProjectReadView } from '@atlassian/jira-issue-field-atlas-project-readview-full/src/ui/atlas-project/index.tsx';
import type { atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditView_fragmentRef$key as AtlasProjectFragment } from '@atlassian/jira-relay/src/__generated__/atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditView_fragmentRef.graphql';
import type { atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef$key as AtlasProjectWithIsEditableFragment } from '@atlassian/jira-relay/src/__generated__/atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import type { atlasProject_issueFieldAtlasProject_AtlasProjectField_LinkProjectToWorkItemMutation as LinkProjectToWorkItemMutation } from '@atlassian/jira-relay/src/__generated__/atlasProject_issueFieldAtlasProject_AtlasProjectField_LinkProjectToWorkItemMutation.graphql';
import { useIntl } from '@atlassian/jira-intl';
import type {
	AtlasProjectInlineEditViewProps,
	AtlasProjectInlineEditViewWithIsEditableProps,
	LinkedProject,
} from './types.tsx';
import messages from './messages.tsx';

/** Determine if the updated edit view value is equal to our original value before submitting a mutation. */
const isEqualOption = (a: LinkedProject | null, b: LinkedProject | null) => a?.id === b?.id;

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-labels-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

/** Link mutation error types that have specific handling implemented */
const LINK_MUTATION_ERROR_TYPES = ['CHILD_ITEMS_LINKED_TO_ANOTHER_PROJECT' as const];
type LinkMutationErrorType = (typeof LINK_MUTATION_ERROR_TYPES)[number];

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `AtlasProjectInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [AtlasProjectInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const AtlasProjectInlineEditViewWithIsEditable = ({
	attributes,
	editViewPopup,
	editViewPopupAlignBlock,
	fragmentRef,
	isEditable,
	isEditing: startWithEditViewOpen = false,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	readViewFitContainerHeight,
	popupPlacement = 'bottom-end',
	spacing = 'compact',
}: AtlasProjectInlineEditViewWithIsEditableProps) => {
	const { showFlag } = useFlagService();
	const { formatMessage } = useIntl();

	// #region Relay
	const data = useFragment<AtlasProjectWithIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef on JiraTownsquareProjectField {
				id
				name
				type
				issue {
					id
				}
				project {
					id
					atlasProjectName: name
				}
				...atlasProject_issueFieldAtlasProjectReadviewFull_AtlasProjectReadView
			}
		`,
		fragmentRef,
	);

	// TODO: Confirm error extension type "TownsquareProjectsWorkItemAlreadyLinkedMutationErrorExtension" below once the edge case has been implemented in the backend
	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commitLinkMutation] = useMutation<LinkProjectToWorkItemMutation>(graphql`
		mutation atlasProject_issueFieldAtlasProject_AtlasProjectField_LinkProjectToWorkItemMutation(
			$input: TownsquareProjectsAddJiraWorkItemLinkInput!
		) {
			projects_addJiraWorkItemLink(input: $input) {
				success
				errors {
					message
					extensions {
						errorType
						statusCode
						__typename
						... on TownsquareProjectsWorkItemAlreadyLinkedMutationErrorExtension {
							canReplace
							project {
								id
								name
							}
						}
					}
				}
				workItem {
					id
					fields {
						edges {
							node {
								... on JiraTownsquareProjectField {
									...atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef
								}
							}
						}
					}
				}
			}
		}
	`);
	// #endregion Relay

	// #region Common state
	const { id: uniqueFieldId, name, type: fieldType, project, issue } = data;

	const initialValue = useMemo(
		() =>
			project
				? {
						id: project.id,
						name: project.atlasProjectName ?? '',
					}
				: null,
		[project],
	);
	const [updatedValue, setUpdatedValue] = useState<LinkedProject | null>(initialValue);
	const { overriding } = useInlineEditFieldInjections();
	const { overrideLabel, overrideIsEditable } = overriding;

	const fieldName = useMemo(() => overrideLabel(name), [overrideLabel, name]);

	const isFieldEditable = useMemo(
		() => overrideIsEditable(isEditable),
		[overrideIsEditable, isEditable],
	);
	// #endregion Common state

	// #region Mutation handlers
	type HandleMutationErrorProps = {
		errorType: LinkMutationErrorType;
		canReplace: boolean;
	};

	const handleMutationError = useCallback(
		({ errorType, canReplace }: HandleMutationErrorProps) => {
			if (errorType === 'CHILD_ITEMS_LINKED_TO_ANOTHER_PROJECT') {
				if (canReplace) {
					// TODO: Trigger replace link confirmation modal: https://jplat.atlassian.net/browse/ANIP-99
				} else {
					showFlag({
						type: 'error',
						title: formatMessage(messages.childIssueAlreadyLinkedNoReplaceTitle),
						description: formatMessage(messages.childIssueAlreadyLinkedNoReplaceDescription),
						isAutoDismiss: false,
						messageId:
							'issue-field-atlas-project-inline-edit-full.ui.atlas-project.show-flag.error.child-issue-already-linked-to-another-project',
						messageType: 'transactional',
						actions: [
							{
								content: formatMessage(messages.childIssueAlreadyLinkedNoReplaceAction),
								href: 'https://support.atlassian.com/platform-experiences/docs/link-goals-and-projects-to-work-in-jira/',
								target: '_blank',
							},
						],
					});
				}
			}
		},
		[formatMessage, showFlag],
	);

	const handleSubmit = useCallback(
		(value: LinkedProject | null, { onSuccess, onFail }: OnSubmitCallbacks) => {
			// Early return if no change
			if (isEqualOption(value, initialValue)) {
				return;
			}

			const issueId = issue?.id;
			const projectId = value?.id;

			// Early return if we don't have the required IDs to call the mutation
			if (!issueId || !projectId) {
				onFail();
				return;
			}

			onSubmit?.(value);

			commitLinkMutation({
				variables: {
					input: {
						workItemId: issueId,
						projectId,
					},
				},
				onCompleted: (response, graphqlErrors) => {
					if (graphqlErrors && graphqlErrors.length > 0) {
						onFail();
						return;
					}

					const mutationResponse = response.projects_addJiraWorkItemLink;

					if (mutationResponse?.success) {
						onSuccess();
						return;
					}

					const { errorType, canReplace } = mutationResponse?.errors?.[0].extensions || {};
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const linkMutationErrorType = (errorType || '') as LinkMutationErrorType;

					if (LINK_MUTATION_ERROR_TYPES.includes(linkMutationErrorType)) {
						handleMutationError({
							errorType: linkMutationErrorType,
							canReplace: canReplace || false,
						});
					} else {
						onFail();
					}
				},
				onError: (error: Error) => {
					onFail(error);
				},
				optimisticUpdater: (store) => {
					const issueDataId = RelayDataID({ id: issueId }, 'JiraIssue');
					const projectDataId = RelayDataID({ id: projectId }, 'TownsquareProject');
					if (!issueDataId || !projectDataId) {
						return;
					}
					const issueRecord = store.get(issueDataId);
					if (!issueRecord) {
						return;
					}
					const projectRecord = store.get(projectDataId);
					if (!projectRecord) {
						return;
					}
					issueRecord.setLinkedRecord(projectRecord, 'project');
				},
			});

			/* todo: convert from Atlaskit shape to AGG-friendly shape.
			 *       pay attention to the data your fragment requests, as the optimistic response must match it
			 *       precisely, otherwise Relay will complain there's a mismatch between them.
			 */
		},
		[initialValue, issue?.id, onSubmit, commitLinkMutation, handleMutationError],
	);
	// #endregion Mutation handlers

	const {
		invalidMessage,
		isEditing,
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChangeAndConfirm,
	} = useFieldInlineEditActions({
		attributes,
		fieldId: uniqueFieldId,
		fieldName,
		fieldType,
		initialValue,
		isValueEqual: isEqualOption,
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		startWithEditViewOpen,
		updatedValue,
	});

	// #region Read view
	const renderReadView = () => (
		<MaybeUFOSegment>
			<AtlasProjectReadView fragmentRef={data} />
		</MaybeUFOSegment>
	);
	// #endregion

	// #region Edit view
	const getEditViewProps = (
		fieldProps: ValidationFieldProps,
	): ProjectPickerWithEntrypointProps => ({
		...fieldProps,
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		onClose: () => {},
		onProjectSelect: (selectedProject) => {
			handleChangeAndConfirm({
				id: selectedProject.id,
				name: selectedProject.name || '',
			});
		},
		onProjectUnlink: () => handleChangeAndConfirm(null),
		linkedProject: project
			? {
					id: project.id,
					name: project.atlasProjectName ?? '',
				}
			: undefined,
		isCompact: spacing === 'compact',
		popupProps: {
			placement: popupPlacement,
		},
	});
	const cloudId = useCloudId();
	const editViewEntryPointParams = useMemo(() => ({ cloudId }), [cloudId]);
	// #endregion

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewEntryPoint={AtlasProjectEditViewEntrypoint}
			editViewEntryPointParams={editViewEntryPointParams}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
			hideActionButtons
		/>
	);
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [AtlasProjectInlineEditViewProps](./types.tsx)
 */
export const AtlasProjectInlineEditView = ({
	fragmentRef,
	...props
}: AtlasProjectInlineEditViewProps) => {
	// #region Relay
	const data = useFragment<AtlasProjectFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditView_fragmentRef on JiraTownsquareProjectField {
				...atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);
	// #endregion

	return (
		<AtlasProjectInlineEditViewWithIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
